/**
 * IntakeLinkStatusFilter — pills horizontales 5 options pour filtrer la liste.
 *
 * Décision PO 9.N.3.A : pills (pas <select>) — visibilité immédiate des 5
 * statuts. Sur mobile, scroll horizontal naturel via `overflow-x-auto` (pas
 * d'indicateur custom — pattern moderne respect natif iOS/Android).
 *
 * a11y :
 *   - <nav role="tablist"> + chaque pill <button role="tab" aria-selected>
 *   - aria-controls pointe vers la table (id côté parent)
 *   - focus-visible:ring-2 brand-500
 *   - touch-manipulation pour hit area mobile ≥ 44pt
 */

import type { IntakeLinkStatusFilter as Status } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";

const OPTIONS: ReadonlyArray<Status> = [
  "all",
  "active",
  "expired",
  "revoked",
  "quota_reached",
];

type Props = {
  value: Status;
  onChange: (next: Status) => void;
  /** ID de la zone contrôlée (table) pour aria-controls. Optionnel. */
  controlsId?: string;
};

export function IntakeLinkStatusFilter({ value, onChange, controlsId }: Props) {
  const { t } = useTranslation();
  return (
    <nav
      role="tablist"
      aria-label={t("intake_links.filters.status_label")}
      className="flex gap-1.5 overflow-x-auto pb-1"
    >
      {OPTIONS.map((opt) => {
        const selected = value === opt;
        return (
          <button
            key={opt}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={controlsId}
            onClick={() => onChange(opt)}
            className={[
              "shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
              "touch-manipulation",
              selected
                ? "border-brand-600 bg-brand-600 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
            ].join(" ")}
          >
            {t(`intake_links.filters.options.${opt}`)}
          </button>
        );
      })}
    </nav>
  );
}
